import { Control, useWatch } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface RecurringInvoiceConfigProps {
  control: Control<any>;
  disabled?: boolean;
}

const RecurringInvoiceConfig = ({
  control,
  disabled,
}: RecurringInvoiceConfigProps) => {
  const frequency = useWatch({ control, name: "frequency" }) || 1;
  const dueDateType = useWatch({ control, name: "dueDateType" }) || "immediate";
  const endCondition = useWatch({ control, name: "endCondition" }) || "never";

  return (
    <div className="space-y-4 border-t pt-4">
      <FormField
        control={control}
        name="dueDateType"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel className="font-semibold text-gray-700">
              Payment Due
            </FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value || "immediate"}
                disabled={disabled}
                className="flex items-center gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="immediate" id="immediate" />
                  <Label
                    htmlFor="immediate"
                    className="font-normal cursor-pointer"
                  >
                    On receipt
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="net_days" id="net_days" />
                  <Label
                    htmlFor="net_days"
                    className="font-normal cursor-pointer"
                  >
                    Within
                  </Label>
                  <FormField
                    control={control}
                    name="dueDateDays"
                    render={({ field: daysField }) => (
                      <Input
                        type="number"
                        min={1}
                        max={180}
                        {...daysField}
                        value={daysField.value ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "") {
                            daysField.onChange(undefined);
                          } else {
                            const parsed = parseInt(value, 10);
                            daysField.onChange(
                              isNaN(parsed) ? undefined : parsed,
                            );
                          }
                        }}
                        disabled={disabled || dueDateType !== "net_days"}
                        className="w-16 h-9 rounded-lg border-gray-200 text-center focus:border-blue-500 focus:ring-blue-500"
                      />
                    )}
                  />
                  <span className="text-sm text-black">days</span>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="rounded-xl border border-gray-100 bg-gray-50 p-5 space-y-4">
        <FormField
          control={control}
          name="frequency"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel className="font-semibold text-gray-700">
                Repeat every
              </FormLabel>
              <div className="mt-2 grid grid-cols-[0.7fr_1fr] gap-3.5">
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 1)
                    }
                    disabled={disabled}
                    className="h-10 w-full rounded-lg border-gray-200"
                  />
                </FormControl>
                <FormField
                  control={control}
                  name="frequencyUnit"
                  render={({ field }) => (
                    <FormControl>
                      <div className="h-10 w-full flex items-center text-sm text-black">
                        {frequency === 1 ? "Month" : "Months"}
                        <input type="hidden" {...field} value="months" />
                      </div>
                    </FormControl>
                  )}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="endCondition"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel className="font-semibold text-gray-700">
                Ends
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value || "never"}
                  disabled={disabled}
                  className="flex flex-wrap items-center gap-4"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="never" id="never" />
                    <Label
                      htmlFor="never"
                      className="font-normal cursor-pointer"
                    >
                      Never
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="after_count" id="after_count" />
                    <Label
                      htmlFor="after_count"
                      className="font-normal cursor-pointer"
                    >
                      After
                    </Label>
                    <FormField
                      control={control}
                      name="endAfterCount"
                      render={({ field: countField }) => (
                        <Input
                          type="number"
                          min={1}
                          {...countField}
                          onChange={(e) =>
                            countField.onChange(
                              parseInt(e.target.value) || null,
                            )
                          }
                          disabled={disabled || endCondition !== "after_count"}
                          className="h-9 w-16 rounded-lg border-gray-200 text-center disabled:bg-gray-100"
                        />
                      )}
                    />
                    <span className="text-sm text-black">invoices</span>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormDescription className="mt-3 flex items-center gap-1 text-xs text-gray-500">
                First invoice will be created immediately
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default RecurringInvoiceConfig;
